/**
 * usePricingWorkflow Hook
 * Integrates the unified pricing workflow service with React components
 *
 * Features:
 * - Restores status from last database execution on mount
 * - Subscribes to real-time status updates via pub/sub
 * - Polls database periodically to catch missed updates
 * - Shows recent completion status for 30 seconds before reverting to idle
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getUnifiedPricingWorkflowService,
  WorkflowStatus,
  WorkflowResult,
  WorkflowOptions,
  WorkflowExecutionRecord,
} from '../services/unifiedPricingWorkflowService';

type UsePricingWorkflowOptions = {
  propertyId: string;
  autoPushOnRuleSave?: boolean;
  pollIntervalMs?: number;
};

export type UsePricingWorkflowReturn = {
  status: WorkflowStatus;
  isExecuting: boolean;
  lastResult: WorkflowResult | null;
  lastExecution: WorkflowExecutionRecord | null;
  executionHistory: WorkflowExecutionRecord[];
  error: string | null;
  autoPushEnabled: boolean;
  setAutoPushEnabled: (enabled: boolean) => void;
  executeNow: (options?: WorkflowOptions) => Promise<WorkflowResult | null>;
  triggerOnRuleSave: (
    ruleId: string,
    options?: WorkflowOptions
  ) => Promise<WorkflowResult | null>;
  refreshHistory: (updateStatus?: boolean) => Promise<void>;
};

const AUTO_PUSH_STORAGE_KEY = 'pricing_workflow_auto_push';
const COMPLETED_STATUS_DISPLAY_MS = 30000;
const DEFAULT_POLL_INTERVAL_MS = 5000;

export function usePricingWorkflow(
  options: UsePricingWorkflowOptions
): UsePricingWorkflowReturn {
  const {
    propertyId,
    autoPushOnRuleSave = true,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  } = options;

  const [status, setStatus] = useState<WorkflowStatus>('idle');
  const [isExecuting, setIsExecuting] = useState(false);
  const [lastResult, setLastResult] = useState<WorkflowResult | null>(null);
  const [lastExecution, setLastExecution] =
    useState<WorkflowExecutionRecord | null>(null);
  const [executionHistory, setExecutionHistory] = useState<
    WorkflowExecutionRecord[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [autoPushEnabled, setAutoPushEnabledState] = useState(() => {
    const stored = localStorage.getItem(AUTO_PUSH_STORAGE_KEY);
    return stored !== null ? stored === 'true' : autoPushOnRuleSave;
  });

  const completedTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastKnownExecutionIdRef = useRef<string | null>(null);
  const historySignatureRef = useRef<string | null>(null);

  const workflowService = useMemo(() => getUnifiedPricingWorkflowService(), []);

  const updateStatusFromExecution = useCallback(
    (execution: WorkflowExecutionRecord | null) => {
      if (!execution) {
        setStatus('idle');
        setIsExecuting(false);
        return;
      }

      const isActive = [
        'started',
        'fetching',
        'calculating',
        'pushing',
      ].includes(execution.status);

      if (isActive) {
        setStatus(execution.status as WorkflowStatus);
        setIsExecuting(true);
        setError(null);
      } else if (
        execution.status === 'completed' ||
        execution.status === 'failed'
      ) {
        const completedAt = execution.completed_at
          ? new Date(execution.completed_at)
          : null;
        const now = new Date();
        const timeSinceCompletion = completedAt
          ? now.getTime() - completedAt.getTime()
          : Infinity;

        if (timeSinceCompletion < COMPLETED_STATUS_DISPLAY_MS) {
          setStatus(execution.status as WorkflowStatus);
          setIsExecuting(false);

          if (execution.status === 'failed' && execution.errors?.length > 0) {
            setError(execution.errors[0].error);
          } else {
            setError(null);
          }

          setLastResult({
            success: execution.status === 'completed',
            executionId: execution.id,
            triggerType: execution.trigger_type,
            triggerSource: execution.trigger_source || undefined,
            status: execution.status as WorkflowStatus,
            startedAt: new Date(execution.started_at),
            completedAt: completedAt || undefined,
            pricesFetched: execution.prices_fetched,
            pricesCalculated: execution.prices_calculated,
            pricesPushed: execution.prices_pushed,
            pricesSkipped: execution.prices_skipped,
            errors: execution.errors || [],
            priceChanges:
              (
                execution.metadata as {
                  priceChanges?: WorkflowResult['priceChanges'];
                }
              )?.priceChanges || [],
          });

          if (completedTimeoutRef.current) {
            clearTimeout(completedTimeoutRef.current);
          }
          const remainingTime =
            COMPLETED_STATUS_DISPLAY_MS - timeSinceCompletion;
          completedTimeoutRef.current = setTimeout(() => {
            setStatus('idle');
          }, remainingTime);
        } else {
          setStatus('idle');
          setIsExecuting(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    console.log(
      `[usePricingWorkflow] Setting up subscription for property ${propertyId}`
    );
    const unsubscribe = workflowService.subscribeToStatus(
      (propId, newStatus, result) => {
        console.log(
          `[usePricingWorkflow] Received status update: ${newStatus} for property ${propId} (listening for ${propertyId})`
        );
        if (propId === propertyId) {
          console.log(
            `[usePricingWorkflow] Status matches! Updating state to: ${newStatus}`
          );

          if (completedTimeoutRef.current) {
            clearTimeout(completedTimeoutRef.current);
            completedTimeoutRef.current = null;
          }

          setStatus(newStatus);
          setIsExecuting(
            newStatus === 'started' ||
              newStatus === 'fetching' ||
              newStatus === 'calculating' ||
              newStatus === 'pushing'
          );
          if (result) {
            setLastResult(result as WorkflowResult);
          }
          if (newStatus === 'failed' && result?.errors?.length) {
            setError(result.errors[0].error);
          } else if (newStatus === 'completed') {
            setError(null);
            completedTimeoutRef.current = setTimeout(() => {
              setStatus('idle');
            }, COMPLETED_STATUS_DISPLAY_MS);
          }
        }
      }
    );

    return () => {
      unsubscribe();
      if (completedTimeoutRef.current) {
        clearTimeout(completedTimeoutRef.current);
      }
    };
  }, [workflowService, propertyId]);

  useEffect(() => {
    const currentStatus = workflowService.getWorkflowStatus(propertyId);
    if (currentStatus !== 'idle') {
      setStatus(currentStatus);
      setIsExecuting(workflowService.isPropertyExecuting(propertyId));
    }
  }, [workflowService, propertyId]);

  const refreshHistory = useCallback(
    async (updateStatus = false) => {
      try {
        const lastExecutionResult =
          await workflowService.getLastExecution(propertyId);
        if (!lastExecutionResult.success) return;
        const execution = lastExecutionResult.data ?? null;

        // The history rows carry up to 100 price changes each (~300KB for
        // 10), so refetch them only when the latest execution moved on.
        const historySignature = execution
          ? `${execution.id}:${execution.status}`
          : 'none';
        if (historySignature !== historySignatureRef.current) {
          const historyResult = await workflowService.getExecutionHistory(
            propertyId,
            10
          );
          if (historyResult.success && historyResult.data) {
            historySignatureRef.current = historySignature;
            setExecutionHistory(historyResult.data);
          }
        }

        if (!execution) return;
        setLastExecution(execution);

        if (updateStatus) {
          const isNewExecution =
            execution.id !== lastKnownExecutionIdRef.current;
          lastKnownExecutionIdRef.current = execution.id;

          if (
            isNewExecution ||
            ['started', 'fetching', 'calculating', 'pushing'].includes(
              execution.status
            )
          ) {
            updateStatusFromExecution(execution);
          }
        }
      } catch (err) {
        console.error('[usePricingWorkflow] Failed to refresh history:', err);
      }
    },
    [workflowService, propertyId, updateStatusFromExecution]
  );

  useEffect(() => {
    refreshHistory(true);
  }, [refreshHistory]);

  useEffect(() => {
    const pollWhileVisible = () => {
      if (document.visibilityState === 'visible') refreshHistory(true);
    };
    const pollInterval = setInterval(pollWhileVisible, pollIntervalMs);
    document.addEventListener('visibilitychange', pollWhileVisible);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', pollWhileVisible);
    };
  }, [refreshHistory, pollIntervalMs]);

  const setAutoPushEnabled = useCallback((enabled: boolean) => {
    setAutoPushEnabledState(enabled);
    localStorage.setItem(AUTO_PUSH_STORAGE_KEY, String(enabled));
  }, []);

  const executeNow = useCallback(
    async (
      workflowOptions?: WorkflowOptions
    ): Promise<WorkflowResult | null> => {
      setError(null);
      try {
        const result = await workflowService.triggerManual(
          propertyId,
          workflowOptions
        );
        if (result.success && result.data) {
          setLastResult(result.data);
          await refreshHistory();
          return result.data;
        } else {
          const errorMsg = result.error?.message || 'Workflow execution failed';
          setError(errorMsg);
          return null;
        }
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMsg);
        return null;
      }
    },
    [workflowService, propertyId, refreshHistory]
  );

  const triggerOnRuleSave = useCallback(
    async (
      ruleId: string,
      workflowOptions?: WorkflowOptions
    ): Promise<WorkflowResult | null> => {
      if (!autoPushEnabled) {
        console.log(
          '[usePricingWorkflow] Auto-push disabled, skipping rule save trigger'
        );
        return null;
      }

      setError(null);
      try {
        const result = await workflowService.triggerOnRuleSave(
          propertyId,
          ruleId,
          workflowOptions
        );
        if (result.success && result.data) {
          setLastResult(result.data);
          await refreshHistory();
          return result.data;
        } else {
          const errorMsg = result.error?.message || 'Workflow execution failed';
          setError(errorMsg);
          return null;
        }
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMsg);
        return null;
      }
    },
    [workflowService, propertyId, autoPushEnabled, refreshHistory]
  );

  return {
    status,
    isExecuting,
    lastResult,
    lastExecution,
    executionHistory,
    error,
    autoPushEnabled,
    setAutoPushEnabled,
    executeNow,
    triggerOnRuleSave,
    refreshHistory,
  };
}

export default usePricingWorkflow;
